import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha, Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, InputAdornment,
  Link, Snackbar, Stack, TextField, Typography
} from '@mui/material';
import { ExpandMore as ExpandIcon, Search as SearchIcon } from '@mui/icons-material';
import { apiGetAll, apiPost } from 'api/service';
import { getPassiveIntel } from 'api/intel';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';

const GREEN = '#22c55e';
const MONO = 'var(--fuzzscan-ui-font-mono)';
const SOURCES = [['crtsh', 'Certificates'], ['dns', 'DNS'], ['whois', 'WHOIS'], ['ip_rep', 'IP reputation'], ['shodan', 'Shodan'], ['asn', 'ASN']];
const SHOW_STEP = 25;

const hostOf = (raw) => {
  const v = String(raw || '').trim().toLowerCase();
  try { return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `//${v}`, 'http://x').hostname || v; } catch { return v; }
};
const first = (v) => (Array.isArray(v) ? v[0] : v) || '';
const niceDate = (s) => { const d = new Date(s); return Number.isNaN(d.getTime()) ? String(s || '') : d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }); };

// Short status text for the source strip.
function sourceNote(id, s) {
  if (!s) return { dot: '#475569', text: 'not queried' };
  if (s.status === 'no_key') return { dot: '#64748b', text: 'no key' };
  if (s.status === 'error') return { dot: '#ef4444', text: 'error' };
  const text = {
    crtsh: `${s.total_certs ?? 0} certs`,
    dns: `${(s.hostsearch?.length || 0) + (s.dnslookup?.length || 0)} records`,
    shodan: `${s.ports?.length || 0} ports`,
    asn: `${s.asns?.length || 0} network${(s.asns?.length || 0) === 1 ? '' : 's'}`
  }[id] || 'ok';
  return { dot: GREEN, text };
}

export default function PassiveIntelPage() {
  const { currentTargetId, targets } = useFuzzScanTarget();
  const userTarget = useMemo(() => (targets || []).find((t) => String(t.id) === String(currentTargetId)), [targets, currentTargetId]);
  const target = userTarget?.target;

  const [domain, setDomain] = useState('');
  const [sources, setSources] = useState(['crtsh', 'dns', 'whois', 'ip_rep', 'shodan']);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [known, setKnown] = useState(new Set()); // subdomains already on the current target
  const [error, setError] = useState('');
  const [shown, setShown] = useState(SHOW_STEP);
  const [adding, setAdding] = useState(false);
  const [snack, setSnack] = useState('');

  useEffect(() => {
    if (target && target.type !== 'mobile' && target.value) setDomain(hostOf(target.value));
  }, [target]);

  useEffect(() => {
    if (!currentTargetId) { setKnown(new Set()); return; }
    apiGetAll(`/api/assets/?user_target=${currentTargetId}`)
      .then((rows) => setKnown(new Set(rows.filter((a) => a.type === 'subdomain').map((a) => hostOf(a.value)))))
      .catch(() => setKnown(new Set()));
  }, [currentTargetId]);

  const run = async () => {
    const t = domain.trim();
    if (!t) { setError('Enter a domain or IP.'); return; }
    setError(''); setLoading(true); setResult(null); setShown(SHOW_STEP);
    try { setResult(await getPassiveIntel(t, sources.join(','))); }
    catch (e) { setError(e?.message || 'Request failed.'); }
    finally { setLoading(false); }
  };
  const toggleSource = (id) => setSources((p) => (p.includes(id) ? p.filter((s) => s !== id) : [...p, id]));

  const src = result?.sources || {};
  const crt = src.crtsh; const dns = src.dns; const who = src.whois; const rep = src.ip_rep; const sh = src.shodan; const asn = src.asn;

  // Every host seen in certificates or DNS, with its IP when DNS gave one.
  const hosts = useMemo(() => {
    const map = new Map();
    (crt?.subdomains || []).forEach((h) => map.set(hostOf(h), ''));
    (dns?.hostsearch || []).forEach((line) => {
      const [h, ip] = line.split(',');
      if (h) map.set(hostOf(h), (ip || '').trim());
    });
    return [...map.entries()].map(([host, ip]) => ({ host, ip, isNew: !known.has(host) })).sort((a, b) => Number(b.isNew) - Number(a.isNew) || a.host.localeCompare(b.host));
  }, [crt, dns, known]);
  const fresh = hosts.filter((h) => h.isNew);

  // Plain-language findings.
  const keyPoints = useMemo(() => {
    if (!result) return [];
    const out = [];
    if (fresh.length && target) out.push(`${fresh.length} subdomain${fresh.length === 1 ? ' is' : 's are'} in public records but not on your target yet.`);
    if (sh?.status === 'ok' && sh.vulns?.length) out.push(`Shodan lists ${sh.vulns.length} known vulnerabilit${sh.vulns.length === 1 ? 'y' : 'ies'} on ${sh.ip || 'the host'} (${sh.vulns.slice(0, 3).join(', ')}${sh.vulns.length > 3 ? '…' : ''}).`);
    const exp = first(who?.parsed?.['Expiration Date']);
    if (exp) {
      const days = Math.round((new Date(exp).getTime() - Date.now()) / 86400000);
      if (!Number.isNaN(days)) {
        if (days < 0) out.push(`The domain registration expired ${-days} days ago.`);
        else if (days < 180) out.push(`The domain registration expires in ${days} days.`);
      }
    }
    if (rep?.status === 'ok' && rep.proxy) out.push('The IP address is a known proxy or VPN.');
    if (sh?.status === 'no_key') out.push('Shodan was skipped because no key is set. Add one in Integrations for ports and vulnerabilities.');
    return out;
  }, [result, fresh.length, target, sh, who, rep]);

  const addNew = async () => {
    if (!target?.id || !fresh.length) return;
    setAdding(true);
    try {
      const res = await apiPost('/api/assets/bulk_create_manual/', { target: target.id, type: 'subdomain', values: fresh.map((h) => h.host), label: 'Passive intel' });
      setKnown((k) => new Set([...k, ...fresh.map((h) => h.host)]));
      setSnack(`Added ${res.created ?? fresh.length} subdomain${(res.created ?? fresh.length) === 1 ? '' : 's'} to ${userTarget?.name || target.value}.`);
    } catch (e) {
      setError(e?.message || 'Could not add to the target.');
    } finally {
      setAdding(false);
    }
  };

  const kpis = result ? [
    ['Subdomains', crt?.unique_subdomains ?? hosts.length, '#93c5fd'], ['Certificates', crt?.total_certs ?? 0, 'inherit'],
    ['DNS records', (dns?.hostsearch?.length || 0) + (dns?.dnslookup?.length || 0), '#4ade80'],
    ['Open ports', sh?.ports?.length || 0, '#fcd34d'], ['CVEs', sh?.vulns?.length || 0, '#fca5a5']
  ] : [];

  const owner = result ? [
    ['Registrar', first(who?.parsed?.Registrar)], ['Created', first(who?.parsed?.['Creation Date']) && niceDate(first(who.parsed['Creation Date']))],
    ['Expires', first(who?.parsed?.['Expiration Date']) && niceDate(first(who.parsed['Expiration Date']))],
    ['Organisation', first(who?.parsed?.['Registrant Organization'])],
    ['Network', rep?.status === 'ok' ? rep.as : asn?.asns?.[0] && `AS${asn.asns[0].asn} ${asn.asns[0].name || ''}`],
    ['Hosting', rep?.status === 'ok' ? `${rep.org || rep.isp || ''}${rep.hosting ? ' (data centre)' : ''}` : ''],
    ['Location', rep?.status === 'ok' ? [rep.city, rep.regionName, rep.country].filter(Boolean).join(', ') : '']
  ].filter(([, v]) => v) : [];

  return (
    <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Passive Intel</Typography>
          <Typography variant="body2" color="text.secondary">What public sources say about a domain or IP, without touching the target</Typography>
        </Box>
        <Stack direction="row" gap={1.2} flexWrap="wrap">
          <TextField value={domain} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()} placeholder="example.com"
            sx={{ minWidth: 300 }} InputProps={{ sx: { fontFamily: MONO, height: 46 }, startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: GREEN }} /></InputAdornment> }} />
          <Button variant="contained" disabled={loading || !sources.length} onClick={run} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ height: 46, px: 3, bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>{loading ? 'Investigating…' : 'Investigate'}</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Sources</Typography>
        {SOURCES.map(([id, label]) => {
          const on = sources.includes(id);
          const note = result ? sourceNote(id, src[id]) : null;
          return (
            <Chip key={id} clickable onClick={() => toggleSource(id)}
              icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: note ? note.dot : on ? GREEN : '#475569', ml: '10px !important' }} />}
              label={<>{label}{note && <Box component="span" sx={{ ml: 0.8, fontSize: 11, color: 'text.secondary' }}>{note.text}</Box>}</>}
              sx={{ height: 36, fontWeight: 600, border: 1, borderColor: on ? alpha(GREEN, 0.5) : 'divider', bgcolor: on ? alpha(GREEN, 0.06) : 'transparent', color: on ? 'text.primary' : 'text.secondary' }} />
          );
        })}
      </Stack>

      {!result && !loading && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 4, p: 6, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Enter a domain and press Investigate</Typography>
          <Typography variant="body2" color="text.secondary">We read certificates, DNS, WHOIS and more from public sources. Nothing is sent to the target.</Typography>
        </Box>
      )}

      {result && (
        <Stack spacing={3}>
          <Box sx={{ borderRadius: 4, p: 2.5, border: 1, borderColor: keyPoints.length ? alpha('#ef4444', 0.4) : 'divider', bgcolor: keyPoints.length ? alpha('#ef4444', 0.07) : 'background.paper' }}>
            <Stack direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
              <Chip size="small" label="KEY POINTS" sx={{ fontWeight: 700, bgcolor: alpha(keyPoints.length ? '#ef4444' : GREEN, 0.2), color: keyPoints.length ? '#fca5a5' : '#4ade80' }} />
              <Box sx={{ flex: 1, minWidth: 240 }}>
                {keyPoints.length === 0 && <Typography>Nothing stands out in the public data for this domain.</Typography>}
                {keyPoints.map((p) => <Typography key={p} sx={{ lineHeight: 1.7 }}>• {p}</Typography>)}
              </Box>
            </Stack>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
            {kpis.map(([label, value, color]) => (
              <Box key={label} sx={{ border: 1, borderColor: 'divider', borderRadius: 3.5, p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 800, fontFamily: MONO, color, mt: 0.5 }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Stack direction={{ xs: 'column', lg: 'row' }} gap={2.5} alignItems="flex-start">
            <Box sx={{ flex: '2 1 600px', minWidth: 0, width: '100%', border: 1, borderColor: 'divider', borderRadius: 4, bgcolor: 'background.paper', overflow: 'hidden' }}>
              <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Subdomains and hosts</Typography>
                  <Typography variant="body2" color="text.secondary">{hosts.length} found in certificates and DNS{target ? ` · ${fresh.length} new to your target` : ''}</Typography>
                </Box>
                <Stack direction="row" gap={1}>
                  <Button variant="outlined" disabled={!hosts.length} onClick={() => navigator.clipboard.writeText(hosts.map((h) => h.host).join('\n')).then(() => setSnack('Copied.')).catch(() => {})}
                    sx={{ borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Copy</Button>
                  <Button variant="contained" disabled={adding || !target?.id || !fresh.length} onClick={addNew}
                    sx={{ bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>
                    {fresh.length ? `Add ${fresh.length} new to ${userTarget?.name || target?.value}` : 'Nothing new to add'}
                  </Button>
                </Stack>
              </Stack>
              {hosts.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>No subdomains found in the sources you chose.</Typography>}
              {hosts.slice(0, shown).map((h) => (
                <Stack key={h.host} direction="row" alignItems="center" gap={1.5} sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography sx={{ flex: 1, fontFamily: MONO, fontSize: 13, wordBreak: 'break-all' }}>{h.host}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: 12, color: 'text.secondary' }}>{h.ip}</Typography>
                  <Chip size="small" label={h.isNew ? 'NEW' : 'ON TARGET'} sx={{ minWidth: 80, fontWeight: 700, fontSize: 11, bgcolor: alpha(h.isNew ? GREEN : '#94a3b8', 0.16), color: h.isNew ? '#4ade80' : '#94a3b8' }} />
                </Stack>
              ))}
              {hosts.length > shown && <Button onClick={() => setShown((n) => n + SHOW_STEP * 2)} sx={{ m: 1.5, color: GREEN, fontWeight: 700 }}>Show more ({hosts.length - shown} left)</Button>}
            </Box>

            <Stack spacing={2.5} sx={{ flex: '1 1 380px', minWidth: 0, width: '100%' }}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 4, p: 2.5, bgcolor: 'background.paper' }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Who owns it</Typography>
                {owner.length === 0 && <Typography variant="body2" color="text.secondary">No ownership data from the chosen sources.</Typography>}
                {owner.map(([k, v]) => (
                  <Stack key={k} direction="row" justifyContent="space-between" gap={2} sx={{ py: 1, borderTop: 1, borderColor: 'divider', fontSize: 13.5 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{k}</Box><Box component="span" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>{v}</Box>
                  </Stack>
                ))}
              </Box>

              {(sh || sources.includes('shodan')) && (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 4, p: 2.5, bgcolor: 'background.paper' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 700 }}>Exposure seen by Shodan</Typography>
                    {sh?.vulns?.length > 0 && <Chip size="small" label={`${sh.vulns.length} CVEs`} sx={{ fontWeight: 700, bgcolor: alpha('#ef4444', 0.16), color: '#fca5a5' }} />}
                  </Stack>
                  {sh?.status === 'no_key' && (
                    <Alert severity="info">No Shodan key yet. Add one in <Link component={RouterLink} to="/api-keys" sx={{ fontWeight: 700 }}>Integrations</Link> to see open ports and vulnerabilities.</Alert>
                  )}
                  {sh?.status === 'error' && <Typography variant="body2" color="error">{sh.detail}</Typography>}
                  {sh?.status === 'ok' && (
                    <>
                      <Stack direction="row" gap={0.6} flexWrap="wrap" sx={{ mb: 1.5 }}>
                        {(sh.ports || []).map((p) => <Chip key={p} size="small" label={p} sx={{ fontFamily: MONO, height: 24 }} />)}
                        {!(sh.ports || []).length && <Typography variant="body2" color="text.secondary">No open ports reported.</Typography>}
                      </Stack>
                      {(sh.vulns || []).length > 0 && (
                        <Typography variant="body2" sx={{ color: '#fca5a5', fontFamily: MONO, fontSize: 12.5, lineHeight: 1.8 }}>{sh.vulns.slice(0, 8).join('\n')}</Typography>
                      )}
                      {(sh.services || []).slice(0, 6).map((s, i) => (
                        <Typography key={`${s.port}-${i}`} variant="caption" color="text.secondary" display="block">{s.port}/{s.transport || 'tcp'} {[s.product, s.version].filter(Boolean).join(' ')}</Typography>
                      ))}
                    </>
                  )}
                </Box>
              )}
            </Stack>
          </Stack>

          <Accordion disableGutters sx={{ border: 1, borderColor: 'divider', borderRadius: '16px !important', '&:before': { display: 'none' }, bgcolor: 'background.paper' }}>
            <AccordionSummary expandIcon={<ExpandIcon />}><Typography sx={{ fontWeight: 700 }}>Raw data</Typography></AccordionSummary>
            <AccordionDetails>
              {['hostsearch', 'dnslookup', 'reversedns'].filter((k) => dns?.[k]?.length).map((k) => (
                <Box key={k} sx={{ mb: 2 }}>
                  <Typography variant="overline" color="text.secondary">DNS · {k}</Typography>
                  <Box component="pre" sx={{ m: 0, p: 1.5, borderRadius: 2, bgcolor: 'action.hover', fontFamily: MONO, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>{dns[k].join('\n')}</Box>
                </Box>
              ))}
              {who?.raw && (
                <Box>
                  <Typography variant="overline" color="text.secondary">WHOIS</Typography>
                  <Box component="pre" sx={{ m: 0, p: 1.5, borderRadius: 2, bgcolor: 'action.hover', fontFamily: MONO, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>{who.raw}</Box>
                </Box>
              )}
              {!dns?.hostsearch?.length && !dns?.dnslookup?.length && !who?.raw && <Typography variant="body2" color="text.secondary">No raw data from the chosen sources.</Typography>}
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
